import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { loadStripe, type Stripe as StripeJs } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { useAuth } from '../lib/auth';
import { apiFetch } from '../lib/api';

type BillingConfig =
  | { configured: false }
  | { configured: true; publishableKey: string; currency: string };

type Plan = 'monthly' | 'annual';

/**
 * Hard-paywall page. Shown by PostAuthGate when /api/me reports the
 * firm's subscription is `trial_expired`, `past_due`, `canceled`, etc.
 * The api enforces this server-side too — every tenant route 402s
 * with `error: 'subscription_required'` so an expired account can't
 * sneak in by URL-typing.
 *
 * Recovery flow:
 *   1. Pick a plan (or keep current).
 *   2. Enter card details (Stripe Elements).
 *   3. SetupIntent → confirmCardSetup attaches the new PM.
 *   4. POST /api/billing/subscribe (creates new sub if none, or updates
 *      existing past_due via Stripe's smart retries on next webhook).
 *   5. onRenewed() refreshes /me; AppShell takes over.
 */
export function Paywall({
  reason,
  trialEndsAt,
  onRenewed,
}: {
  reason: string;
  trialEndsAt: string | null;
  onRenewed: () => void;
}) {
  const { t } = useTranslation(['register', 'common']);
  const { signOut, user } = useAuth();
  const [billingConfig, setBillingConfig] = useState<BillingConfig | null>(null);
  const [stripePromise, setStripePromise] = useState<Promise<StripeJs | null> | null>(null);

  useEffect(() => {
    apiFetch<BillingConfig>('/api/billing/config')
      .then((c) => {
        setBillingConfig(c);
        if (c.configured) setStripePromise(loadStripe(c.publishableKey));
      })
      .catch(() => setBillingConfig({ configured: false }));
  }, []);

  return (
    <main className="reg-wrap">
      <header className="reg-brand">
        <div className="reg-brand-name">{t('register:brand.name')}</div>
        <div className="reg-brand-sub">{t('register:brand.tagline')}</div>
      </header>
      <section className="reg-panel reg-panel-landing">
        <h1 className="reg-step-title">
          {reason === 'trial_expired'
            ? 'תקופת הניסיון הסתיימה'
            : reason === 'past_due'
              ? 'התשלום נכשל'
              : 'נדרש תשלום להמשך השימוש'}
        </h1>
        <p className="reg-step-sub">
          {reason === 'trial_expired'
            ? `תקופת הניסיון של 30 יום הסתיימה ב-${formatDate(trialEndsAt)}. בחר תוכנית והוסף אמצעי תשלום כדי להמשיך.`
            : reason === 'past_due'
              ? 'הכרטיס הקיים נדחה. עדכן את פרטי התשלום כדי להמשיך.'
              : 'המנוי שלך אינו פעיל. צור מנוי חדש כדי להמשיך.'}
        </p>

        {billingConfig?.configured && stripePromise ? (
          <Elements stripe={stripePromise}>
            <PaywallForm onRenewed={onRenewed} />
          </Elements>
        ) : billingConfig && !billingConfig.configured ? (
          <div className="reg-error">{t('register:step_payment.errors.not_configured')}</div>
        ) : (
          <p className="reg-step-sub">…</p>
        )}

        <div className="reg-foot-row">
          <span>{user?.email}</span>
          <button type="button" className="btn-link" onClick={signOut}>
            {t('common:actions.sign_out')}
          </button>
        </div>
      </section>
    </main>
  );
}

function PaywallForm({ onRenewed }: { onRenewed: () => void }) {
  const { t } = useTranslation('register');
  const stripe = useStripe();
  const elements = useElements();
  const [plan, setPlan] = useState<Plan>('annual');
  const [cardName, setCardName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!stripe || !elements) return;
    if (!cardName.trim()) return setError(t('step_payment.errors.no_name'));
    setBusy(true);
    setError(null);
    try {
      const intent = await apiFetch<{ clientSecret: string }>(
        '/api/billing/setup-intent',
        { method: 'POST', body: JSON.stringify({}) },
      );
      const card = elements.getElement(CardElement);
      if (!card) throw new Error('card_element_missing');
      const { error: stripeErr, setupIntent } = await stripe.confirmCardSetup(
        intent.clientSecret,
        { payment_method: { card, billing_details: { name: cardName.trim() } } },
      );
      if (stripeErr) {
        setError(stripeErr.message ?? t('step_payment.errors.stripe_error'));
        return;
      }
      if (!setupIntent || setupIntent.status !== 'succeeded') {
        setError(t('step_payment.errors.stripe_error'));
        return;
      }
      const paymentMethodId =
        typeof setupIntent.payment_method === 'string'
          ? setupIntent.payment_method
          : (setupIntent.payment_method?.id ?? undefined);
      // Server sets the new card as default and, for an existing past_due
      // subscription, pays the open invoice to reactivate it immediately.
      await apiFetch('/api/billing/subscribe', {
        method: 'POST',
        body: JSON.stringify({ plan, paymentMethodId }),
      });
      onRenewed();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="reg-step" onSubmit={handleSubmit}>
      <div className="reg-plan-grid">
        <PlanButton plan="monthly" selected={plan === 'monthly'} onClick={() => setPlan('monthly')} />
        <PlanButton plan="annual" selected={plan === 'annual'} onClick={() => setPlan('annual')} />
      </div>

      <label className="reg-field">
        <span>
          {t('step_payment.card_name')}
          {t('step_details.required_marker')}
        </span>
        <input
          type="text"
          value={cardName}
          onChange={(e) => setCardName(e.target.value)}
          placeholder={t('step_payment.card_name_placeholder')}
          autoComplete="cc-name"
          required
        />
      </label>

      <label className="reg-field">
        <span>
          {t('step_payment.card_field')}
          {t('step_details.required_marker')}
        </span>
        <div className="reg-card-element">
          <CardElement options={{ hidePostalCode: true }} />
        </div>
      </label>

      <div className="reg-secure-row" aria-hidden="true">🔒 {t('step_payment.secure_row')}</div>
      {error && <div className="reg-error">{error}</div>}

      <button type="submit" className="btn-primary reg-cta" disabled={busy || !stripe}>
        {busy ? t('step_payment.submit_busy') : t('step_payment.submit')}
      </button>
    </form>
  );
}

function PlanButton({
  plan,
  selected,
  onClick,
}: {
  plan: Plan;
  selected: boolean;
  onClick: () => void;
}) {
  const { t } = useTranslation('register');
  const isAnnual = plan === 'annual';
  return (
    <button
      type="button"
      className={`reg-plan-card ${selected ? 'selected' : ''}`}
      onClick={onClick}
      aria-pressed={selected}
    >
      <div className="reg-plan-radio">
        <div className="reg-plan-radio-dot" />
      </div>
      <div className={`reg-plan-badge ${isAnnual ? 'pb-save' : 'pb-monthly'}`}>
        {isAnnual ? t('step_payment.plan_annual_badge') : t('step_payment.plan_monthly_badge')}
      </div>
      <div className="reg-plan-name">
        {isAnnual ? t('step_payment.plan_annual') : t('step_payment.plan_monthly')}
      </div>
      <div className="reg-plan-price">{isAnnual ? '₪1,000' : '₪100'}</div>
      <div className="reg-plan-per">
        {isAnnual ? t('step_payment.plan_annual_period') : t('step_payment.plan_monthly_period')}
      </div>
    </button>
  );
}

function formatDate(iso: string | null): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('he-IL', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}
